import { Grid, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import Provision from '../../../../api/provision';
import Modal from '../../../../components/Modal';
import Table from '../../../../components/Table';
import useApiQuery from '../../../../hooks/useApiQuery';

import type { ErrorType } from '@/types';
import type { ComputeQuota, NetworkQuota, VolumeQuota } from '@/types/cloudTypes';
import type { Product } from '@/types/productTypes';
import type { TableRowData, TableField } from '@/types/tableTypes';

interface ForecastsTableProps {
  product: Product;
  onSubmit: () => void;
  onClose: () => void;
}

interface Forecast {
  cloudId: string;
  compute: ComputeQuota;
  volume: VolumeQuota;
  network: NetworkQuota;
  [key: string]: unknown;
}

const ForecastTable = ({ product, onSubmit, onClose }: ForecastsTableProps): React.JSX.Element => {
  const { juiceGroup, name } = product.details;
  const intl = useIntl();

  const {
    data: forecasts = null,
    error: fetchError,
    isLoading,
    makeApiCall: fetchForecasts,
  } = useApiQuery<Forecast[]>(Provision.fetchForecastCapacity);
  const { makeApiCall: createVirtualClouds } = useApiQuery(Provision.createVirtualClouds, {
    errorMessage: 'forecastsApplyFailure',
    successMessage: 'forecastsApplySuccess',
  });

  useEffect(() => {
    fetchForecasts(juiceGroup);
  }, [juiceGroup, fetchForecasts]);

  const ramToMegabytes = (ram: number): number => ram * 1024;

  const fields: TableField[] = [
    { title: 'Cloud Id', id: 'cloudId' },
    { title: 'vCPU', id: 'cores' },
    { title: 'RAM (GB)', id: 'ram' },
    { title: 'Storage (GB)', id: 'gigabytes' },
    { title: 'Floating IPs', id: 'floatingIps' },
  ];

  const handleClick = async (): Promise<void> => {
    if (!forecasts) return;

    const transformedForecasts = forecasts.map(forecast => ({
      ...forecast,
      compute: {
        ...forecast.compute,
        ram: ramToMegabytes(forecast.compute.ram),
      },
    }));

    const data: unknown = await createVirtualClouds(transformedForecasts);
    if (data) onSubmit();
  };

  const rows: TableRowData[] | undefined = forecasts?.map(forecast => ({
    key: forecast.cloudId,
    data: [
      { field: 'cloudId', value: forecast.cloudId },
      { field: 'cores', value: forecast.compute.cores },
      { field: 'ram', value: forecast.compute.ram },
      { field: 'gigabytes', value: forecast.volume.gigabytes },
      { field: 'floatingIps', value: forecast.network.floatingIps },
    ],
  }));

  return (
    <Modal
      maxWidth='sm'
      title={intl.formatMessage({ id: 'productForecasts' }, { name: name as string })}
      hideActions={isLoading}
      disabled={isLoading || !forecasts?.length}
      onClose={onClose}
      onSubmit={() => {
        void handleClick();
      }}
      submitLabel='Apply'
      error={fetchError}
    >
      {forecasts || isLoading ? (
        <Grid container>
          <Grid
            item
            xs={12}
          >
            <Table
              fields={fields}
              rows={rows ?? []}
              search={false}
              isLoading={isLoading}
              error={fetchError as ErrorType}
            />
          </Grid>
        </Grid>
      ) : (
        <Typography pb={2}>{intl.formatMessage({ id: 'forecastsNotFound' })}</Typography>
      )}
    </Modal>
  );
};

export default ForecastTable;
